import { Button } from "../../components/ui/button";
import {
  Building,
  Mail,
  Phone,
  MapPin,
  ExternalLink,
  Facebook,
  Twitter,
  Linkedin,
  Youtube
} from "lucide-react";

export default function Footer() {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer id="contact" className="bg-slate-900 text-white">
      {/* Main Footer Content */}
      <div className="container mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* About PMRDA Column */}
          {/* About PMRDA Column */}
          <div className="lg:col-span-1 space-y-4">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center">
                <Building className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold">SMKMC</h3>
                <p className="text-sm text-blue-200">Maharashtra Govt.</p>
              </div>
            </div>

            <h4 className="text-lg font-semibold text-white mb-3">
              About SMKMC
            </h4>

            <p className="text-blue-200 text-sm leading-relaxed mb-4">
              <strong className="text-white">Mission:</strong> To digitally plan, track, and manage municipal infrastructure projects — including roads, gutters, and footpaths — through sustainable, transparent, and technology-driven governance.
            </p>

            <p className="text-blue-200 text-sm leading-relaxed">
              <strong className="text-white">Vision:</strong> Creating a world-class municipal infrastructure system with efficient execution, transparent processes, and citizen-centric services across all wards of Sangli.
            </p>
          </div>


          {/* Quick Links Column */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-white mb-4">
              Quick Links
            </h4>
            <nav className="space-y-3">
              <button
                onClick={() => scrollToSection("home")}
                className="block text-blue-200 hover:text-accent transition-colors text-sm"
              >
                Home
              </button>
              <button
                onClick={() => scrollToSection("about")}
                className="block text-blue-200 hover:text-accent transition-colors text-sm"
              >
                About Initiative
              </button>
              <button
                onClick={() => scrollToSection("features")}
                className="block text-blue-200 hover:text-accent transition-colors text-sm"
              >
                Features
              </button>
              <button
                onClick={() => scrollToSection("explore")}
                className="block text-blue-200 hover:text-accent transition-colors text-sm"
              >
                Blockchain Explorer
              </button>
              <button
                onClick={() => scrollToSection("contact")}
                className="block text-blue-200 hover:text-accent transition-colors text-sm"
              >
                Contact Us
              </button>
            </nav>
          </div>

          {/* Government Links Column */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-white mb-4">
              Government Links
            </h4>
            <nav className="space-y-3">
              <a
                href="https://www.maharashtra.gov.in"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-blue-200 hover:text-accent transition-colors text-sm"
              >
                Maharashtra Govt. Portal
                <ExternalLink className="w-3 h-3 ml-1" />
              </a>
              <a
                href="https://pmaymis.gov.in/pmaymis2_2024/pmaydefault.aspx"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-blue-200 hover:text-accent transition-colors text-sm"
              >
                Pradhan Mantri Awas Yojana (Urban)
                <ExternalLink className="w-3 h-3 ml-1" />
              </a>
              <a
                href="https://aaplesarkar.mahaonline.gov.in/en"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-blue-200 hover:text-accent transition-colors text-sm"
              >
                Right to Services (RTS)
                <ExternalLink className="w-3 h-3 ml-1" />
              </a>
              <a
                href="https://pmrda.gov.in"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-blue-200 hover:text-accent transition-colors text-sm"
              >
                PMRDA Official Website
                <ExternalLink className="w-3 h-3 ml-1" />
              </a>
            </nav>
          </div>

          {/* Contact Info Column */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-white mb-4">
              Contact Information
            </h4>

            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                <div>
                  <p className="text-sm text-white font-medium">Address</p>
                  <p className="text-sm text-blue-200 leading-relaxed">

                    Sangli Miraj Kupwad Mahanagarpalika,
                    Municipal Office, Sangli, Maharashtra, India
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-accent flex-shrink-0" />
                <div>
                  <p className="text-sm text-white font-medium">Phone</p>
                  <a
                    href="tel:020-25933344"
                    className="text-sm text-blue-200 hover:text-accent transition-colors"
                  >
                    020-25933344
                  </a>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-accent flex-shrink-0" />
                <div>
                  <p className="text-sm text-white font-medium">Phone</p>
                  <a
                    href="tel:+020-29991277"
                    className="text-sm text-blue-200 hover:text-accent transition-colors"
                  >
                    020-29991277
                  </a>
                </div>
              </div>
            </div>

            {/* Social Media */}
            <div className="pt-4">
              <p className="text-sm text-white font-medium mb-3">Follow Us</p>
              <div className="flex space-x-3">
                <a
                  href="https://www.facebook.com/OfficialPMRDA"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 flex items-center justify-center rounded-full"
                  style={{ backgroundColor: "#1877F2" }}
                >
                  <Facebook className="w-4 h-4 text-white" />
                </a>
                <a
                  href="https://x.com/OfficialPMRDA?t=vIDEy-Vh7naDfEzoIlzzUw&s=09"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 flex items-center justify-center rounded-full"
                  style={{ backgroundColor: "#1DA1F2" }}
                >
                  <Twitter className="w-4 h-4 text-white" />
                </a>
                <a
                  href="https://www.linkedin.com/company/pune-metropolitan-region-development-authority/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 flex items-center justify-center rounded-full"
                  style={{ backgroundColor: "#0A66C2" }}
                >
                  <Linkedin className="w-4 h-4 text-white" />
                </a>
                <a
                  href="https://www.youtube.com/@pmrda"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 flex items-center justify-center rounded-full"
                  style={{ backgroundColor: "#FF0000" }}
                >
                  <Youtube className="w-4 h-4 text-white" />
                </a>
              </div>
            </div>

          </div>
        </div>
      </div>



      {/* Bottom Footer */}
      <div className="border-t border-slate-700 bg-slate-800">
        <div className="container mx-auto px-6 py-6">
          <div className="flex flex-col lg:flex-row justify-between items-center space-y-4 lg:space-y-0">
            <p className="text-sm text-blue-200 text-center lg:text-left">
              © {currentYear} Sangli Miraj Kupwad Mahanagarpalika. All Rights Reserved.
            </p>

            <div className="flex items-center space-x-6 text-sm text-blue-200">
              <button className="hover:text-accent transition-colors">
                Privacy Policy
              </button>
              <button className="hover:text-accent transition-colors">
                Terms of Service
              </button>
              <button className="hover:text-accent transition-colors">
                Accessibility
              </button>
            </div>
          </div>

          {/* Government Badge */}
          <div className="mt-4 pt-4 border-t border-slate-700 text-center">
            <div className="inline-flex items-center space-x-2 text-xs text-blue-300">
              <Building className="w-4 h-4" />
              <span>An initiative of Government of Maharashtra | Digital India | Smart Cities Mission</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}